import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import useUpdatePipeline from 'redux/slices/pipelines/hooks/useUpdatePipeline';
import { editPipelineSchema } from 'redux/slices/pipelines/constants/validationSchema';
import SourceImageStep from '../Wizard/steps/SourceImageStep';
import TargetContainerStep from '../Wizard/steps/TargetContainerStep';
import NameStep from '../Wizard/steps/NameStep';

const validate = values => {
	try {
		editPipelineSchema.validateSync(values, { abortEarly: false });
		return {};
	} catch (validationError) {
		const result = {};
		(validationError.inner ?? []).forEach(e => {
			if (e.path && !result[e.path]) {
				result[e.path] = e.message;
			}
		});
		return result;
	}
};

// AC-19 — a separate inline form/dialog (NOT the wizard, per OQ-2) that re-maps
// source/target/name and saves via PATCH (partial, RV-4). Reuses the wizard's own step
// components (SourceImageStep/TargetContainerStep/NameStep) since they only depend on a
// generic {values, errors, touched, setFieldValue} shape, not Formik itself — so the same
// two-level 1:1-exclusion target picker (AC-6) applies here too (TC-DPW-033), while
// `excludePipelineId` keeps this pipeline's OWN current container selectable.
//
// R1/AC-2 (pre-impl review) — TargetContainerStep now takes a `setValues` prop (not
// `setFieldValue`) for its App-Instance handler. This dialog's OWN `useState` setter
// below is already a valid updater-capable `setValues` (React's setState accepts the
// `prev => next` updater form natively) — this dialog was already correct before this
// slice and MUST NOT regress; passing `setValues` (not `setFieldValue`) here keeps it so.
const PipelineEditDialog = ({ visible, pipeline, appInstanceId, onClose }) => {
	const [values, setValues] = useState({ Name: '', ImageName: '', AppInstanceId: '', ContainerAppId: '' });
	const [touched, setTouched] = useState({});
	const [saveError, setSaveError] = useState(null);

	const updateMutate = useUpdatePipeline();

	useEffect(() => {
		if (visible) {
			setValues({
				Name: pipeline?.Name ?? '',
				ImageName: pipeline?.ImageName ?? '',
				AppInstanceId: appInstanceId ?? '',
				ContainerAppId: pipeline?.ContainerAppId ?? ''
			});
			setTouched({});
			setSaveError(null);
		}
	}, [visible, pipeline, appInstanceId]);

	const errors = useMemo(() => validate(values), [values]);
	const isValid = useMemo(() => editPipelineSchema.isValidSync(values), [values]);

	const setFieldValue = (field, value) => setValues(prev => ({ ...prev, [field]: value }));
	const setFieldTouched = (field, isTouched = true) => setTouched(prev => ({ ...prev, [field]: isTouched }));

	const handleSave = async () => {
		setTouched({ Name: true, ImageName: true, AppInstanceId: true, ContainerAppId: true });
		if (!isValid || !pipeline) return;

		setSaveError(null);

		// PATCH is partial (RV-4) — only send fields that actually changed.
		const payload = { id: pipeline.Id };
		const trimmedName = values.Name.trim();
		if (trimmedName !== pipeline.Name) payload.Name = trimmedName;
		if (values.ImageName !== pipeline.ImageName) payload.ImageName = values.ImageName;
		if (Number(values.ContainerAppId) !== pipeline.ContainerAppId) {
			payload.ContainerAppId = Number(values.ContainerAppId);
		}

		if (Object.keys(payload).length === 1) {
			// Nothing changed — no-op close, no network call.
			onClose();
			return;
		}

		try {
			await updateMutate.mutateAsync(payload);
			toast.success('Pipeline updated successfully');
			onClose();
		} catch (error) {
			const status = error?.response?.status;
			setSaveError(
				status === 400
					? error?.response?.data?.message ?? 'That container may already have a live pipeline, or a field is invalid — adjust and try again.'
					: error?.message ?? 'Something went wrong updating this pipeline.'
			);
		}
	};

	return (
		<Dialog open={visible} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Edit Pipeline</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={5} mt={1}>
					<SourceImageStep values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} />
					<TargetContainerStep values={values} errors={errors} touched={touched} setValues={setValues} excludePipelineId={pipeline?.Id} />
					<NameStep values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} createError={saveError} />
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={updateMutate.isPending}>
					Cancel
				</Button>
				<LoadingButton variant="contained" loading={updateMutate.isPending} disabled={!isValid} onClick={handleSave}>
					Save
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default React.memo(PipelineEditDialog);

PipelineEditDialog.propTypes = {
	visible: PropTypes.bool,
	pipeline: PropTypes.object,
	appInstanceId: PropTypes.string,
	onClose: PropTypes.func
};
